import { ErrorAttr } from "@services/common/types";
import { Link } from "react-router-dom";
import { NeedHelp } from "@screens/shared/need-help";
import { errorMessage } from "@utils/mutation-handler";
import { toast } from "react-hot-toast";
import { useState } from "react";
import { Anchor, Button, Form, Input } from "@edgelogistics/web-ui";
import { IconButton, InputAdornment } from "@mui/material";
import {
  IoEye,
  IoEyeOff,
  IoLockClosedOutline,
  IoPersonOutline,
} from "react-icons/io5";
import { animated, useSpring } from "react-spring";
import { login, useAuth } from "@context/auth";
import { object, string } from "yup";

type LoginFormType = {
  email: string;
  password: string;
};

const PasswordInput = (): React.ReactElement => {
  const [visible, setVisible] = useState(false);

  return (
    <Input
      name="password"
      placeholder="Password"
      type={visible ? "text" : "password"}
      variant="outlined"
      classNames={{
        input: "xl:mt-[14px]",
      }}
      inputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setVisible((v) => !v)}
              edge="end"
            >
              {visible ? (
                <IoEye className="text-base text-black" />
              ) : (
                <IoEyeOff className="text-base text-black" />
              )}
            </IconButton>
          </InputAdornment>
        ),
        startAdornment: (
          <InputAdornment position="start">
            <IoLockClosedOutline className="text-base text-black" />
          </InputAdornment>
        ),
      }}
      rootInputProps={{
        className: "h-6 px-1 py-1.5 text-[0.875rem]",
      }}
    />
  );
};

export const Login = (): React.ReactElement => {
  const { setData } = useAuth();
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);

  const styles = useSpring({
    from: { x: 512, opacity: 0 },
    to: { x: 0, opacity: 1 },
  });

  return (
    <div className="flex w-full  justify-center lg:justify-end">
      <animated.div style={styles}>
        <Form<LoginFormType>
          onSubmit={(values: LoginFormType) => {
            setIsLoginLoading(true);
            login(setData, values).catch((error: ErrorAttr) => {
              setIsLoginLoading(false);
              toast.error(
                errorMessage("There was an error while logging in!")(error),
                {
                  className: "bg-white text-black text-sm max-w-2xl",
                }
              );
            });
          }}
          schema={object().shape({
            email: string()
              .required("Username is required")
              .email("Must be a valid email"),
            password: string().required("Password is required"),
          })}
          className="w-full max-w-[380px] rounded-xl bg-white py-8 px-8 md:w-[327px] lg:w-[380px] lg:px-12 lg:py-5"
        >
          <h1 className="mb-5 text-2xl font-bold lg:mb-[35.5px] lg:text-3.5xl">
            Welcome!
          </h1>
          <div className="grid gap-0.5">
            <Input
              name="email"
              placeholder="Username"
              variant="outlined"
              inputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IoPersonOutline className="text-base text-black" />
                  </InputAdornment>
                ),
              }}
              rootInputProps={{
                className: "h-6 px-1 py-1.5 text-[0.875rem]",
              }}
            />

            <PasswordInput />
          </div>
          <div className="flex flex-row items-center justify-between xl:mt-[14px]">
            <Anchor
              to="/recover-account"
              component={Link}
              className="w-[max-content] truncate p-0 font-sans text-[13px] font-semibold capitalize text-black"
              appearance="link"
            >
              Forgot password
            </Anchor>

            <NeedHelp />
          </div>

          <Button
            className="mb-1 mt-3 max-h-10 w-full text-sm font-normal lg:mb-7 lg:mt-2"
            status={isLoginLoading ? "loading" : "default"}
          >
            Login
          </Button>
        </Form>
      </animated.div>
    </div>
  );
};
